<template>
	<div class="relative w-full">
		<input
			:id="id"
			:value="inputValue"
			:type="type"
			:disabled="disabled"
			:name="name"
			class="peer w-full appearance-none rounded-lg border border-base-200 bg-transparent px-2.5 pb-2.5 pt-4 text-base text-base-content hover:border-primary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:bg-sp-base-100 disabled:text-base-300"
			:class="{
				'pl-10': $slots.left,
				'!border-error focus:!ring-error': errorMessage,
			}"
			placeholder=" "
			:pattern="pattern"
			:minlength="minlength"
			:maxlength="maxlength"
			@input="onChange"
			@blur="onBlur"
		/>
		<label
			:for="id"
			class="pointer-events-none absolute left-2 top-1.5 z-10 origin-[0] -translate-y-4 scale-75 transform rounded-lg bg-white px-2 text-sm text-info-200 duration-300 peer-placeholder-shown:pointer-events-auto peer-placeholder-shown:top-[27px] peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-sp-grey-300 peer-focus:top-1.5 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-primary"
			:class="{ 'pl-8': $slots.left && !inputValue }"
		>
			{{ placeholder }}
			<span v-if="required" class="text-error"> * </span>
		</label>
		<span
			v-if="$slots.left"
			class="absolute left-3 top-3.5 z-10 flex cursor-pointer items-center"
		>
			<slot name="left"></slot>
		</span>
		<span
			v-if="$slots.right"
			class="absolute right-3 top-4 flex cursor-pointer items-center"
		>
			<slot name="right"></slot>
		</span>
		<InputError
			v-if="showFieldError"
			:message="errorMessage"
			class="relative top-0 mx-2 -mt-2.5 mb-1 w-fit rounded bg-white px-2"
		/>
	</div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { InputError } from '@/Components';
import { useField } from 'vee-validate';

export interface InputProps {
	type?: string;
	disabled?: boolean;
	required?: boolean;
	id: string;
	name: string;
	placeholder?: string;
	pattern?: string;
	minlength?: number;
	maxlength?: number;
	showFieldError?: boolean;
}

const props = withDefaults(defineProps<InputProps>(), {
	type: 'text',
	disabled: false,
	required: false,
	placeholder: undefined,
	pattern: undefined,
	minlength: undefined,
	maxlength: undefined,
	showFieldError: false,
});

const emit = defineEmits(['blur']);

const isFocused = ref(false);

const {
	value: inputValue,
	handleChange,
	handleBlur,
	errorMessage,
} = useField<string>(() => props.name);

const onChange = (event: Event) => {
	handleChange(event, false);
};

const onBlur = (event: Event) => {
	handleBlur(event, true);
	isFocused.value = false;
	emit('blur');
};
</script>

<style scoped>
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

/* Firefox */
input[type='number'] {
	-moz-appearance: textfield;
}
</style>
